import { createLazyModal } from '@/components/ModalLoader';

/**
 * Lazy Modals - 자주 사용되는 모달들을 lazy loading으로 최적화
 * 
 * 메모리 절약 효과:
 * - 모달이 실제로 열릴 때까지 로드하지 않음
 * - 초기 번들에서 모달 코드 제거 (150-200KB 절약)
 * - 사용자 상호작용 성능 향상
 */

// FAQ Modal - 자주 사용되는 큰 모달
export const LazyFAQModal = createLazyModal(
  () => import('@/components/FAQModal'),
  // 큰 모달을 위한 커스텀 로딩 UI
  <div className="flex items-center justify-center min-h-[400px]">
    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
  </div>
);

// Profile Edit Modal - 사용자 설정
export const LazyProfileEditModal = createLazyModal(
  () => import('@/components/ProfileEditModal')
);

// Checklist Modal - 크고 복잡한 모달
export const LazyChecklistModal = createLazyModal(
  () => import('@/components/ChecklistModal'),
  // 체크리스트를 위한 스켈레톤
  <div className="p-6 space-y-4">
    <div className="h-6 bg-gray-200 rounded animate-pulse"></div>
    {[...Array(6)].map((_, i) => (
      <div key={i} className="flex items-center space-x-3">
        <div className="w-4 h-4 bg-gray-200 rounded animate-pulse"></div>
        <div className="h-4 bg-gray-200 rounded flex-1 animate-pulse"></div>
      </div>
    ))}
  </div>
);

// Edit Booking Modal - 예약 수정
export const LazyEditBookingModal = createLazyModal(
  () => import('@/components/EditBookingModal')
);

// Status Change Modal - 상태 변경
export const LazyStatusChangeModal = createLazyModal(
  () => import('@/components/StatusChangeModal')
);

// Product Request Modal - 상품 요청
export const LazyProductRequestModal = createLazyModal(
  () => import('@/components/ProductRequestModal')
);

// Cancel Booking Dialog - 예약 취소
export const LazyCancelBookingDialog = createLazyModal(
  () => import('@/components/CancelBookingDialog')
);

// Privacy Policy Modal - 개인정보 처리방침
// 이 모달들은 자체적으로 Dialog를 포함하고 있으므로 직접 lazy loading
import { lazy, Suspense } from 'react';

const PrivacyPolicyModalLazy = lazy(() => import('@/components/modals/PrivacyPolicyModal'));
const RefundPolicyModalLazy = lazy(() => import('@/components/modals/RefundPolicyModal'));

export const LazyPrivacyPolicyModal = ({ children }: { children: React.ReactNode }) => (
  <Suspense fallback={null}>
    <PrivacyPolicyModalLazy>{children}</PrivacyPolicyModalLazy>
  </Suspense>
);

// Refund Policy Modal - 환불 정책
export const LazyRefundPolicyModal = ({ children }: { children: React.ReactNode }) => (
  <Suspense fallback={null}>
    <RefundPolicyModalLazy>{children}</RefundPolicyModalLazy>
  </Suspense>
);

/**
 * Modal preloading utilities
 * 자주 사용되는 모달들을 사전 로딩하는 함수들
 */
// eslint-disable-next-line react-refresh/only-export-components
export const preloadCommonModals = () => {
  // 사용자가 가장 많이 사용하는 모달들 사전 로딩
  if ('requestIdleCallback' in window) {
    window.requestIdleCallback(() => {
      import('@/components/FAQModal');
      import('@/components/ProfileEditModal');
    });
  }
};

// eslint-disable-next-line react-refresh/only-export-components
export const preloadBookingModals = () => {
  // 예약 관련 모달들 사전 로딩
  if ('requestIdleCallback' in window) {
    window.requestIdleCallback(() => {
      import('@/components/EditBookingModal');
      import('@/components/CancelBookingDialog');
    });
  }
};

/**
 * Admin Lazy Modals - 관리자 전용 모달들
 * admin-complete chunk에 포함되도록 별도 관리
 */
export const LazyBookingCancelDialog = createLazyModal(
  () => import('@/components/admin/BookingCancelDialog')
);

export default {
  LazyFAQModal,
  LazyProfileEditModal,
  LazyChecklistModal,
  LazyEditBookingModal,
  LazyStatusChangeModal,
  LazyProductRequestModal,
  LazyCancelBookingDialog,
  LazyPrivacyPolicyModal,
  LazyRefundPolicyModal,
  LazyBookingCancelDialog,
  preloadCommonModals,
  preloadBookingModals,
};